<template>
	<div class="content">
		<header class="page-header">
			<span class="page-header-emoji">🏠</span>
			<div class="page-header-text">
				<div class="page-header-title">家的记忆</div>
				<div class="page-header-sub">{{ listSummary }}</div>
			</div>
		</header>

		<div class="filter-bar">
			<button
				v-for="item in rangeOptions"
				:key="item.value"
				type="button"
				class="filter-chip"
				:class="{ 'filter-chip--active': activeRange === item.value }"
				@click="changeRange(item.value)"
			>
				{{ item.label }}
			</button>
		</div>

		<div v-if="memoryList.length > 0" class="search-wrap">
			<van-search
				v-model="searchText"
				shape="round"
				background="transparent"
				placeholder="搜索标题、详情或时间"
				clearable
				@search="onSearch"
				@clear="clearSearch"
			/>
		</div>

		<div v-if="memoryList.length === 0" class="empty-tip">
			<span class="empty-tip-emoji">📔</span>
			<span>还没有家的记忆</span>
			<button type="button" class="empty-tip-btn" @click="openAddDialog">记录第一条</button>
		</div>

		<div v-else-if="filteredMemoryList.length === 0" class="empty-tip">
			<span class="empty-tip-emoji">🔍</span>
			<span>没有找到匹配的记忆</span>
		</div>

		<div v-else class="timeline-section">
			<div class="timeline-spine" aria-hidden="true"></div>

			<div
				v-for="entry in displayTimeline"
				:key="'memory-' + entry.item.id"
				class="timeline-item"
				:class="[
					'timeline-item--' + entry.side,
					{ 'timeline-item--match': isSearchActive },
				]"
				:style="getCardStyle(entry.themeIndex)"
			>
				<div class="timeline-time">{{ formatMemoryTime(getMemoryTime(entry.item)) }}</div>
				<div class="timeline-card-line">
					<div class="timeline-card-wrap">
						<div
							class="timeline-card"
							@click="openDetail(entry.item)"
							@touchstart="(e) => handleTouchStart(e, entry.item)"
							@touchend="handleTouchEnd"
							@touchmove="handleTouchMove"
							@contextmenu.prevent
							@selectstart.prevent
						>
							<div class="timeline-card-title">{{ entry.item.title }}</div>
						</div>
					</div>
					<span class="timeline-connector" aria-hidden="true"></span>
					<span class="timeline-dot" aria-hidden="true"></span>
					<div class="timeline-line-spacer" aria-hidden="true"></div>
				</div>
			</div>
		</div>

		<button type="button" class="add-fab" aria-label="添加记忆" @click="openAddDialog">+</button>

		<van-dialog
			v-model="showAddDialog"
			title="记录记忆"
			show-cancel-button
			:before-close="beforeAddClose"
			@cancel="closeAddDialog"
		>
			<div class="add-dialog-body">
				<van-field v-model="title" label="记忆" placeholder="例如：星泽第一次叫爸爸" maxlength="80" />
				<van-field
					v-model="content"
					type="textarea"
					label="详情"
					placeholder="补充当时的情况（可选）"
					rows="2"
					autosize
					maxlength="500"
				/>
				<div class="add-time" @click="openTimeSelector">
					发生时间：{{ displayTime }}
					<span class="add-time-hint">（点击修改）</span>
				</div>
			</div>
			<van-popup v-model="showDate" position="bottom" round :z-index="3020" get-container="body">
				<van-datetime-picker
					v-model="selectTime"
					type="datetime"
					title="选择发生时间"
					@cancel="showDate = false"
					@confirm="selectTimeConfirm"
				/>
			</van-popup>
		</van-dialog>

		<van-dialog
			v-model="showDetailDialog"
			title="记忆详情"
			:show-cancel-button="false"
			confirm-button-text="关闭"
			@confirm="showDetailDialog = false"
		>
			<div class="detail-dialog-body" v-if="detailItem">
				<div class="detail-title">{{ detailItem.title }}</div>
				<div class="detail-time">{{ formatMemoryTime(getMemoryTime(detailItem)) }}</div>
				<div class="detail-content">{{ detailItem.content || '暂无详情' }}</div>
			</div>
		</van-dialog>

		<van-dialog
			v-model="showDeleteDialog"
			class="delete-memory-dialog"
			title="删除这条记忆？"
			show-cancel-button
			@confirm="confirmDelete"
			@cancel="showDeleteDialog = false"
			@opened="clearTextSelection"
		>
			<div class="delete-dialog-body" @selectstart.prevent @contextmenu.prevent>
				<div>{{ deleteItem?.title }}</div>
				<div class="delete-dialog-sub">{{ deleteItem ? formatMemoryTime(getMemoryTime(deleteItem)) : '' }}</div>
			</div>
		</van-dialog>
	</div>
</template>

<script>
import { getHomeMemoryList, addHomeMemory, deleteHomeMemory } from '../util/api'
import { Notify } from 'vant'

const CARD_THEMES = [
	{ accent: '#34c3ff', soft: '#e8f7ff' },
	{ accent: '#ff6b84', soft: '#fff0f3' },
	{ accent: '#2dd4bf', soft: '#ecfdf8' },
	{ accent: '#fb923c', soft: '#fff7ed' },
]

export default {
	name: 'HomeMemory',
	data() {
		return {
			memoryList: [],
			title: '',
			content: '',
			displayTime: '',
			selectTime: new Date(),
			showDate: false,
			showAddDialog: false,
			saveSuccessful: false,
			activeRange: 'all',
			rangeOptions: [
				{ label: '近3月', value: '3m' },
				{ label: '近1年', value: '1y' },
				{ label: '全部', value: 'all' },
			],
			pressTimer: null,
			showDeleteDialog: false,
			deleteItem: null,
			showDetailDialog: false,
			detailItem: null,
			suppressClick: false,
			searchText: '',
		}
	},
	computed: {
		listSummary() {
			const total = this.memoryList.length
			const query = this.searchText.trim()
			if (!query) {
				return `共 ${total} 条家的记忆`
			}
			return `找到 ${this.filteredMemoryList.length} / ${total} 条`
		},
		isSearchActive() {
			return this.searchText.trim().length > 0
		},
		filteredMemoryList() {
			const query = this.searchText.trim().toLowerCase()
			if (!query) {
				return this.memoryList
			}
			return this.memoryList.filter((item) => this.matchesSearch(item, query))
		},
		timelineGroups() {
			const groups = {}
			this.filteredMemoryList.forEach((item) => {
				const dateKey = this.formatDateKey(this.getMemoryTime(item))
				if (!groups[dateKey]) {
					groups[dateKey] = []
				}
				groups[dateKey].push(item)
			})
			return Object.keys(groups)
				.sort((a, b) => new Date(b) - new Date(a))
				.map((dateKey) => ({
					dateKey,
					memories: groups[dateKey].sort(
						(a, b) => new Date(this.getMemoryTime(b)) - new Date(this.getMemoryTime(a))
					),
				}))
		},
		displayTimeline() {
			const entries = []
			let sideIndex = 0
			this.timelineGroups.forEach((group) => {
				group.memories.forEach((item) => {
					entries.push({
						item,
						side: sideIndex % 2 === 0 ? 'right' : 'left',
						themeIndex: sideIndex,
					})
					sideIndex += 1
				})
			})
			return entries
		},
	},
	methods: {
		changeRange(value) {
			this.activeRange = value
			this.loadMemories()
		},
		getMemoryTime(item) {
			return item.memory_time || item.event_time || item.create_time
		},
		matchesSearch(item, query) {
			const title = (item.title || '').toLowerCase()
			const content = (item.content || '').toLowerCase()
			const timeText = this.formatMemoryTime(this.getMemoryTime(item)).toLowerCase()
			return title.includes(query) || content.includes(query) || timeText.includes(query)
		},
		onSearch() {
			// van-search 已双向绑定，保留以便移动端键盘搜索
		},
		clearSearch() {
			this.searchText = ''
		},
		getDateRange() {
			if (this.activeRange === 'all') {
				return {}
			}
			const end = new Date()
			const start = new Date()
			if (this.activeRange === '3m') {
				start.setMonth(start.getMonth() - 3)
			} else if (this.activeRange === '1y') {
				start.setFullYear(start.getFullYear() - 1)
			}
			return {
				startDate: this.formatQueryDate(start),
				endDate: this.formatQueryDate(end),
			}
		},
		formatQueryDate(date) {
			return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
		},
		loadMemories() {
			getHomeMemoryList(this.getDateRange()).then((res) => {
				this.memoryList = Array.isArray(res.data) ? res.data : []
			})
		},
		getCardStyle(index) {
			const theme = CARD_THEMES[index % CARD_THEMES.length]
			return {
				'--accent': theme.accent,
				'--accent-soft': theme.soft,
			}
		},
		openDetail(item) {
			if (this.suppressClick) {
				this.suppressClick = false
				return
			}
			this.detailItem = item
			this.showDetailDialog = true
		},
		formatMemoryTime(time) {
			const date = new Date(time)
			if (Number.isNaN(date.getTime())) return time
			const h = String(date.getHours()).padStart(2, '0')
			const min = String(date.getMinutes()).padStart(2, '0')
			return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${h}:${min}`
		},
		formatDateKey(time) {
			const date = new Date(time)
			if (Number.isNaN(date.getTime())) return time
			return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
		},
		openAddDialog() {
			this.title = ''
			this.content = ''
			const now = new Date()
			this.selectTime = now
			this.displayTime = now.toLocaleString()
			this.showDate = false
			this.showAddDialog = true
		},
		closeAddDialog() {
			this.showAddDialog = false
			this.showDate = false
		},
		syncSelectedTime() {
			if (!(this.selectTime instanceof Date) || Number.isNaN(this.selectTime.getTime())) {
				return
			}
			this.displayTime = this.selectTime.toLocaleString()
		},
		beforeAddClose(action, done) {
			if (action !== 'confirm') {
				this.showDate = false
				done()
				return
			}
			if (!this.title || !this.title.trim()) {
				Notify({ type: 'warning', message: '请填写记忆标题' })
				done(false)
				return
			}
			this.syncSelectedTime()
			this.showDate = false
			this.saveSuccessful = false
			addHomeMemory({
				title: this.title.trim(),
				content: this.content.trim() || null,
				memoryTime: this.displayTime,
			}).then((res) => {
				const data = res.data
				if (data?.code === 0) {
					Notify({ type: 'success', message: '记录成功' })
					this.loadMemories()
				} else {
					Notify({ type: 'danger', message: data?.message || '记录失败' })
				}
				this.saveSuccessful = true
			})
			const timer = setInterval(() => {
				if (this.saveSuccessful) {
					clearInterval(timer)
					done()
				}
			}, 80)
		},
		openTimeSelector() {
			const parsed = new Date(this.displayTime)
			this.selectTime = Number.isNaN(parsed.getTime()) ? new Date() : parsed
			this.showDate = true
		},
		selectTimeConfirm(date) {
			this.selectTime = date
			this.syncSelectedTime()
			this.showDate = false
		},
		handleTouchStart(e, item) {
			this.pressTimer = setTimeout(() => {
				this.clearTextSelection()
				this.suppressClick = true
				this.deleteItem = item
				this.showDeleteDialog = true
			}, 800)
		},
		clearTextSelection() {
			const selection = window.getSelection?.()
			if (selection) {
				if (selection.empty) {
					selection.empty()
				} else {
					selection.removeAllRanges()
				}
			}
			if (document.activeElement && document.activeElement !== document.body) {
				document.activeElement.blur()
			}
		},
		handleTouchMove() {
			clearTimeout(this.pressTimer)
		},
		handleTouchEnd() {
			clearTimeout(this.pressTimer)
		},
		confirmDelete() {
			if (!this.deleteItem) return
			deleteHomeMemory({ id: this.deleteItem.id }).then((res) => {
				const data = res.data
				if (data?.code === 0) {
					Notify({ type: 'success', message: '已删除' })
					this.loadMemories()
				} else {
					Notify({ type: 'danger', message: data?.message || '删除失败' })
				}
			})
		},
	},
	mounted() {
		this.displayTime = new Date().toLocaleString()
		this.loadMemories()
	},
}
</script>

<style scoped>
.content {
	--q-bg0: #fff7fb;
	--q-bg1: #f2fbff;
	--q-card: rgba(255, 255, 255, 0.96);
	--q-line: rgba(15, 23, 42, 0.08);
	--q-shadow-sm: 0 6px 14px rgba(17, 24, 39, 0.08);
	--q-radius: 16px;

	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
	overflow: hidden;
	box-sizing: border-box;
	padding: 10px 10px 12px;
	position: relative;
	font-family: "Comic Sans MS", "Microsoft YaHei UI", "PingFang SC", "Hiragino Sans GB", system-ui, -apple-system,
		BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
	background: radial-gradient(120% 90% at 20% 0%, var(--q-bg0) 0%, transparent 60%),
		radial-gradient(120% 90% at 90% 30%, var(--q-bg1) 0%, transparent 55%), #ffffff;
}

.content::before,
.content::after {
	content: "";
	position: fixed;
	z-index: -1;
	pointer-events: none;
	opacity: 0.9;
}

.content::before {
	width: 240px;
	height: 240px;
	left: -90px;
	top: 140px;
	background: radial-gradient(circle at 30% 30%, rgba(255, 184, 220, 0.55), transparent 60%),
		radial-gradient(circle at 70% 70%, rgba(110, 231, 255, 0.45), transparent 62%);
	border-radius: 999px;
}

.content::after {
	width: 280px;
	height: 280px;
	right: -140px;
	bottom: 90px;
	background: radial-gradient(circle at 40% 30%, rgba(196, 255, 214, 0.5), transparent 60%),
		radial-gradient(circle at 70% 70%, rgba(255, 224, 170, 0.45), transparent 62%);
	border-radius: 999px;
}

.page-header {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-shrink: 0;
	margin-bottom: 6px;
	padding: 10px 12px;
	border: 2px solid transparent;
	border-radius: 20px;
	background:
		linear-gradient(var(--q-card), var(--q-card)) padding-box,
		linear-gradient(135deg, rgba(52, 195, 255, 0.45), rgba(255, 107, 132, 0.35), rgba(45, 212, 191, 0.35)) border-box;
	box-shadow: var(--q-shadow-sm);
	position: relative;
}

.page-header::after {
	content: "✨";
	position: absolute;
	right: 14px;
	top: 12px;
	font-size: 16px;
	opacity: 0.85;
	pointer-events: none;
}

.page-header-emoji {
	font-size: 24px;
	line-height: 1;
}

.page-header-title {
	font-size: 16px;
	font-weight: 700;
	color: #1f2937;
}

.page-header-sub {
	font-size: 12px;
	color: #9ca3af;
	margin-top: 2px;
}

.filter-bar {
	display: flex;
	gap: 8px;
	flex-shrink: 0;
	margin-bottom: 6px;
}

.search-wrap {
	flex-shrink: 0;
	margin-bottom: 6px;
	border-radius: 999px;
	overflow: hidden;
	background: rgba(255, 255, 255, 0.88);
	border: 1px solid rgba(52, 195, 255, 0.15);
	box-shadow: var(--q-shadow-sm);
}

.search-wrap /deep/ .van-search {
	padding: 2px 6px;
}

.search-wrap /deep/ .van-search__content {
	background: transparent;
}

.search-wrap /deep/ .van-field__control {
	font-size: 13px;
}

.filter-chip {
	border: 1px solid rgba(52, 195, 255, 0.15);
	background: rgba(255, 255, 255, 0.88);
	color: #6b7280;
	font-size: 12px;
	font-weight: 700;
	padding: 5px 12px;
	border-radius: 999px;
	cursor: pointer;
	font-family: inherit;
}

.filter-chip--active {
	color: #fff;
	border-color: transparent;
	background: linear-gradient(180deg, #34c3ff 0%, #6ee7ff 100%);
	box-shadow: 0 6px 14px rgba(52, 195, 255, 0.28);
}

.empty-tip {
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 10px;
	color: #9ca3af;
	font-size: 14px;
	min-height: 160px;
}

.empty-tip-emoji {
	font-size: 40px;
}

.empty-tip-btn {
	margin-top: 4px;
	border: none;
	background: linear-gradient(180deg, #34c3ff 0%, #6ee7ff 100%);
	color: #fff;
	font-size: 13px;
	font-weight: 700;
	padding: 8px 18px;
	border-radius: 999px;
	font-family: inherit;
	box-shadow: 0 8px 18px rgba(52, 195, 255, 0.24);
}

.timeline-section {
	flex: 1;
	min-height: 0;
	overflow-x: hidden;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
	padding: 8px 6px 52px;
	border: 2px solid transparent;
	border-radius: 20px;
	background:
		linear-gradient(rgba(255, 251, 254, 0.92), rgba(242, 251, 255, 0.88)) padding-box,
		linear-gradient(135deg, rgba(255, 184, 220, 0.4), rgba(110, 231, 255, 0.38), rgba(196, 255, 214, 0.35)) border-box;
	box-shadow: var(--q-shadow-sm);
	position: relative;
}

.timeline-spine {
	position: absolute;
	left: 50%;
	top: 8px;
	bottom: 8px;
	width: 4px;
	transform: translateX(-50%);
	border-radius: 999px;
	background: linear-gradient(
		180deg,
		rgba(255, 184, 220, 0.75) 0%,
		rgba(110, 231, 255, 0.75) 50%,
		rgba(196, 255, 214, 0.75) 100%
	);
	box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.65);
	pointer-events: none;
	z-index: 0;
}

.timeline-item {
	position: relative;
	display: flex;
	flex-direction: column;
	width: 100%;
	box-sizing: border-box;
	padding-bottom: 10px;
	z-index: 1;
}

.timeline-card-line {
	display: flex;
	flex-direction: row;
	align-items: center;
	width: 100%;
}

.timeline-item--right .timeline-card-line {
	flex-direction: row-reverse;
}

.timeline-card-wrap {
	width: calc(50% - 17px);
	flex-shrink: 0;
	display: flex;
	min-width: 0;
}

.timeline-item--left .timeline-card-wrap {
	justify-content: flex-end;
}

.timeline-item--right .timeline-card-wrap {
	justify-content: flex-start;
}

.timeline-item--left .timeline-time {
	align-self: flex-start;
	width: calc(50% - 17px);
	text-align: right;
}

.timeline-item--right .timeline-time {
	align-self: flex-end;
	width: calc(50% - 17px);
	text-align: left;
}

.timeline-connector {
	width: 12px;
	height: 2px;
	background: var(--accent);
	opacity: 0.75;
	border-radius: 999px;
	flex-shrink: 0;
}

.timeline-dot {
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background: var(--accent);
	border: 2px solid #fff;
	box-shadow: 0 0 0 1px var(--accent-soft);
	flex-shrink: 0;
	box-sizing: border-box;
}

.timeline-line-spacer {
	flex: 1;
	min-width: 0;
}

.timeline-time {
	margin-bottom: 3px;
	padding: 0;
	font-size: 9px;
	font-weight: 700;
	color: var(--accent);
	opacity: 0.85;
	line-height: 1.2;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	min-width: 0;
}

.timeline-item--match .timeline-card {
	box-shadow:
		0 0 0 2px var(--accent),
		0 6px 16px rgba(17, 24, 39, 0.08);
}

.timeline-card {
	position: relative;
	display: inline-block;
	max-width: 100%;
	padding: 6px 9px;
	border-radius: 14px;
	background: var(--accent-soft);
	border: none;
	box-shadow: 0 6px 16px rgba(17, 24, 39, 0.08);
	transition: transform 120ms ease;
	cursor: pointer;
	box-sizing: border-box;
	-webkit-touch-callout: none;
	-webkit-user-select: none;
	user-select: none;
	touch-action: manipulation;
}

.timeline-card:active {
	transform: scale(0.98);
}

.timeline-card-title {
	font-size: 12px;
	font-weight: 700;
	color: #2f3440;
	line-height: 1.4;
	word-break: break-word;
	display: -webkit-box;
	-webkit-line-clamp: 3;
	line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-align: left;
	-webkit-user-select: none;
	user-select: none;
}

.add-dialog-body {
	padding: 8px 0 4px;
}

.add-dialog-body /deep/ .van-field {
	background: transparent;
}

.add-time {
	padding: 8px 16px 4px;
	font-size: 13px;
	color: #374151;
}

.add-time-hint {
	color: #9ca3af;
	font-size: 12px;
}

.delete-dialog-body {
	padding: 16px 24px 8px;
	text-align: center;
	font-size: 15px;
	color: #111827;
	-webkit-touch-callout: none;
	-webkit-user-select: none;
	user-select: none;
}

.delete-dialog-sub {
	margin-top: 8px;
	font-size: 12px;
	color: #9ca3af;
}

.content /deep/ .delete-memory-dialog,
.content /deep/ .delete-memory-dialog .van-dialog__content,
.content /deep/ .delete-memory-dialog .van-dialog__message {
	-webkit-user-select: none;
	user-select: none;
}

.detail-dialog-body {
	padding: 8px 20px 16px;
}

.detail-title {
	font-size: 17px;
	font-weight: 700;
	color: #1f2937;
	line-height: 1.5;
	word-break: break-word;
}

.detail-time {
	margin-top: 8px;
	font-size: 12px;
	color: #9ca3af;
}

.detail-content {
	margin-top: 14px;
	padding: 12px 14px;
	border-radius: 14px;
	background: rgba(242, 251, 255, 0.9);
	font-size: 14px;
	color: #4b5563;
	line-height: 1.6;
	white-space: pre-wrap;
	word-break: break-word;
}
</style>
